import { Grid, Link } from "@material-ui/core";
import { isAuthorized } from "components/Authorize/authorizeUtil";
import { permissionProfiles } from 'components/Authorize/permissionProfiles';
import DataTable from "components/DataTable/DataTable";
import PageComponent from "components/Page/PageComponent";
import { userPreferencesComponentIds } from "components/UserPreferences/userPreferencesConsts";
import { useGetAllCarriersQuery } from "features/carriers/carrierSlice";
import { useGetClientsQuery } from "features/clients/clientSlice";
import { FlagEntityTypes } from "features/flag/flagsConsts";
import { useGetLotsQuery } from "features/lots/lotSlice";
import { FlagAsset } from "features/vehicles/Flags/FlagAsset";
import useSortOptions from "hooks/useSortOptions";
import { orderBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import FilterService, { fieldTypes } from "services/filterService";
import { arrayToObject } from "utils/object-util";
import { userHasOneClient } from "../user/userSlice";
import { VehicleLinks } from 'features/vehicles/components/VehicleLinks/VehicleLinks';
import { AddReservation } from "./NewReservation/AddReservation";
import { ReservationEditAction } from "./NewReservation/ReservationEditAction";
import { ReservationOverviewAction } from "./NewReservation/ReservationOverviewAction";
import { reservationPickupTypeList, reservationStatusList } from "./reservationConsts";
import { resetFilters, setFilters, setSearchVal } from "./reservationsFiltersSlice";
import { useGetAllReservationsQuery } from "./reservationSlice";
import { ReservationStatusFilter } from "./ReservationStatusFilter/ReservationStatusFilter";
import { ReservationView } from "./ReservationView/ReservationView";
import { reservationTableStyle } from "./Style";

const ReservationPage = ({ }) => {
    const classes = reservationTableStyle();
    const hasOneClaim = useSelector(userHasOneClient);

    let { data: clients, error: clientError, isLoading: isLoadingClients } = useGetClientsQuery(null);
    let { data: lots, error: lotError, isLoading: isLoadingLots } = useGetLotsQuery();
    let { data: reservations, error: reservationsError, isFetching: isLoadingReservations } = useGetAllReservationsQuery();
    let { data: carriers, error: carriersError, isLoading: isLoadingCarriers } = useGetAllCarriersQuery();

    reservations = reservations || [];
    carriers = carriers || [];

    const canSeeFlags = isAuthorized(permissionProfiles.FLAGS.VIEW_FLAGS);
    const accounts = clients?.flatMap(c => c.clientAccounts);
    const { searchVal, filters } = useSelector(state => state.reservationsFilters);
    const [sortOptions, sortData] = useSortOptions();

    const reservationsLookup = useMemo(() => {
        return arrayToObject(reservations);
    }, [reservations]);

    const dispatch = useDispatch();
    let { id } = useParams();

    const [reservationToView, setReservationToView] = useState();
    const [paramsIdUsed, setParamsIdUsed] = useState(false);

    useEffect(() => {
        //console.log('in use effect')
        if (reservationToView && !isLoadingReservations) {
            setReservationToView(reservationsLookup?.[reservationToView?.id])
        }
        if (id && !paramsIdUsed && !isLoadingReservations) {
            setReservationToView(reservationsLookup?.[id])
            setParamsIdUsed(true)
        }
    }, [isLoadingReservations]);


    const filterConfig = [
        ...(hasOneClaim ? [] : [
            {
                type: fieldTypes.TEXT, size: 2, title: 'Client', key: 'clientId', values: accounts?.filter(c => c.isDefault)?.map(acc => ({
                    id: acc?.clientId,
                    name: acc?.name
                }))
            }]),
        { type: fieldTypes.TEXT, title: 'Status', key: 'status', values: reservationStatusList },
        { type: fieldTypes.LOT, title: 'Pickup Location', key: 'pickupLocationId', values: lots },
        { type: fieldTypes.LOT, title: 'Dropoff Location', key: 'deliveryLocationId', values: lots },
        { type: fieldTypes.TEXT, title: 'Carrier', key: 'carrierId', values: carriers }
    ]

    let subClients = accounts?.filter(a =>
        !a.isDefault &&
        (filters?.clientId && filters?.clientId?.value?.length > 0 ? filters?.clientId.value?.includes(a.clientId) : true)
    )

    if (subClients?.length > 0) {
        filterConfig.splice(1, 0, {
            type: fieldTypes.TEXT,
            size: 2,
            title: 'Sub Client',
            key: 'clientAccountId',
            values: subClients,
            multiple: false
        });
    }

    if (canSeeFlags) {
        filterConfig.push({
            type: fieldTypes.TEXT,
            size: 2,
            title: 'Flagged',
            key: 'flaggedStr',
            values: [{ id: 'Yes', name: 'Yes' }, { id: 'No', name: 'No' }]
        });
    }

    const onDropFilter = (value, prop) => {
        dispatch(setFilters({ ...filters, [prop]: { value } }));
    };

    const onResetFilters = () => {
        dispatch(resetFilters());
    };

    const onSearch = (val) => {
        dispatch(setSearchVal(val));
    };

    const columns = [
        {
            name: "Reservation #", key: "resId", width: 180,
            component: (row) =>
                <>
                    <Link className="link-button" onClick={() => setReservationToView(row)}><div>{row?.resId}</div></Link>
                </>
        },
        { name: "Client", key: "clientId", values: clients },
        { name: "Sub Client", key: "clientAccountId", values: accounts?.filter(a => !a.isDefault) },
        { name: "Status", key: "status", values: reservationStatusList },
        { name: "Pickup Type", key: "pickupType", values: reservationPickupTypeList },
        { name: "Pickup Location", key: "pickupLocationId", values: lots },
        { name: "Dropoff Location", key: "deliveryLocationId", values: lots },
        { name: "Estimate Pick Up Window", key: "pickupDate", component: (row) => row.formattedPickupDate },
        { name: "Actual Pick Up Date", key: "actualPickUpDate", component: (row) => row.formattedActualPickUpDate },
        { name: "Pickup Time", key: "pickupTime" },
        { name: "Estimate Drop off Window", key: "deliveryDate", component: (row) => row.formattedDeliveryDate },
        { name: "Actual Drop Off Date", key: "actualDropOffDate", component: (row) => row.formattedActualDropOffDate },
        { name: "Insurance Expiry Date", key: "insuranceExpiryDate", component: (row) => row.formattedInsuranceExpiryDate },
        { name: "Dropoff Time", key: "deliveryTime" },
        { name: "Poc Name", key: "primaryPocName" },
        { name: "Poc Phone", key: "primaryPocPhone" },
        { name: "Poc Email", key: "primaryPocEmail" },
        { name: "Carrier", key: "carrierId", values: carriers },
        { name: "Asset", width: 200, key: 'ReservationVehicles', component: (row) => <VehicleLinks vehicles={row?.reservationVehicles} /> },
        { name: "Rental Rate", key: "rentalRate", component: (row) => row.formattedRentalRate }
    ]

    if (canSeeFlags) {
        columns.unshift({
            name: '',
            key: 'latestFlag',
            width: 80,
            selectedByDefault: true,
            customizable: false,
            component: (row) => {
                return <FlagAsset
                    iconOnly
                    selectedRows={[row?.id]}
                    flagOn={reservationsLookup?.[row?.id]?.latestFlag}
                    flagObj={reservationsLookup?.[row?.id]?.latestFlag}
                    sourceType={reservationsLookup?.[row?.id]?.latestFlag?.sourceType}
                    getFlagFromApi={false}
                    allData={reservations}
                    entityType={FlagEntityTypes.RESERVATION}
                />
            }
        });
    }

    const getName = (data, id) => data.find(l => l.id === id)?.name;

    const mapExportData = row => {
        const reservations = {};
        columns?.map(col => {
            if (col.name) {
                reservations[col.name] = row?.[col.key]
            }
        });
        reservations['Client'] = getName(clients, row?.clientId);
        reservations['Sub Client'] = getName(accounts, row?.clientAccountId);
        reservations['Status'] = getName(reservationStatusList, row?.status);
        reservations['Pickup Type'] = getName(reservationPickupTypeList, row?.pickupType);
        reservations['Pickup Location'] = getName(lots, row?.pickupLocationId);
        reservations['Dropoff Location'] = getName(lots, row?.deliveryLocationId);
        reservations['Carrier'] = getName(carriers, row?.carrierId);
        reservations['Asset'] = row?.reservationVehicles?.map(a => a.vin || a.assetId)?.join(', ');
        reservations['Estimate Pick Up Window'] = row?.['formattedPickupDate'];
        reservations['Actual Pick Up Date'] = row?.['formattedActualPickUpDate'];
        reservations['Estimate Drop off Window'] = row?.['formattedDeliveryDate'];
        reservations['Actual Drop Off Date'] = row?.['formattedActualDropOffDate'];
        reservations['Insurance Expiry Date'] = row?.['formattedInsuranceExpiryDate'];
        reservations['Rental Rate'] = row?.['formattedRentalRate'];

        return reservations;
    };

    const filteredReservations = FilterService.filter(filters, searchVal, reservations, filterConfig);

    const rowActions = (rowId) => {
        const row = reservationsLookup?.[rowId];
        return [
            {
                component: <ReservationOverviewAction reservation={row} />
            },
            {
                component: <ReservationEditAction reservation={row} />
            },
            // {
            //     component: <ReservationDocumentsAction reservation={row} />
            // },
            // {
            //     component: <ReservationVehicleAction reservation={row} />
            // }
        ]
    };

    const getRowClassName = (rowIndex) => {
        return (canSeeFlags && filteredReservations?.[rowIndex]?.latestFlag) ? classes.flaggedRow : '';
    }

    return (
        <PageComponent>
            <ReservationStatusFilter reservations={reservations} onSelectStatus={(status) => onDropFilter(status, 'status')} />
            <DataTable
                showActionsInMenu
                mapExportData={mapExportData}
                noItemsMessage={isLoadingReservations ? 'Loading...' : 'No reservations found'}
                tableId={userPreferencesComponentIds.RESERVATIONS_TABLE}
                allowColumnCustomization={true}
                withFilterPanel={true}
                dropDownsConfig={filterConfig}
                filters={filters}
                onDropFilter={onDropFilter}
                onSearch={onSearch}
                searchVal={searchVal}
                onResetFilters={onResetFilters}
                columns={columns}
                rows={orderBy(
                    filteredReservations,
                    sortOptions.columnToSort,
                    sortOptions.sortDirection
                )}
                getRowClassName={getRowClassName}
                rowIdentifier='id'
                onSort={sortData}
                sortDirection={sortOptions.sortDirection}
                columnToSort={sortOptions.columnToSort}
                title={'Reservations'}
                actions={rowActions}
                actionButtons={
                    <Grid container direction='row' spacing={1}>
                        <Grid item>
                            <AddReservation />
                        </Grid >
                    </Grid>
                }
            />
            {!!reservationToView && <ReservationView
                onClose={() => setReservationToView()}
                withDialog
                reservation={reservationToView}
            />}
        </PageComponent>
    )
};

export default ReservationPage;
